import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/images/logo.png';
import platformImg from '../assets/images/oil-platform.png';
import '../styles/auth.css';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle } from 'lucide-react';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const { login, error: authError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for return URL in query parameters
  useEffect(() => {
    // If user is already authenticated, redirect to home
    if (isAuthenticated()) {
      navigate('/');
      return;
    }
    
    // Handle session expired message
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('session') === 'expired') {
      setError('Your session has expired. Please log in again.');
    }
  }, [isAuthenticated, navigate, location]);
  
  // Update error message when auth context error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // Validate email format - only alphanumeric with @ and . for @ongc.co.in domain
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.]+@ongc\.co\.in$/;
    return emailRegex.test(email);
  };

  // Validate password format - alphanumeric characters, 8-16 characters long
  const validatePassword = (password) => {
    const passwordRegex = /^[a-zA-Z0-9]{8,16}$/;
    return passwordRegex.test(password);
  };
  
  // Handle email input change
  const handleEmailChange = (e) => {
    const inputValue = e.target.value;
    
    // Filter out any characters that are not alphanumeric, @, or .
    const filteredEmail = inputValue.replace(/[^a-zA-Z0-9@.]/g, '');
    
    setUsername(filteredEmail);
    
    // Clear email error when field is empty
    if (!filteredEmail) {
      setEmailError('');
      return;
    }
    
    // Set error if email format is invalid
    if (!validateEmail(filteredEmail)) {
      setEmailError('Please enter a valid ONGC email address (must end with @ongc.co.in)');
    } else {
      setEmailError('');
    }
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    const inputValue = e.target.value;
    
    // Filter out any characters that are not alphanumeric
    const filteredPassword = inputValue.replace(/[^a-zA-Z0-9]/g, '');
    
    // Limit to maximum 16 characters
    const limitedPassword = filteredPassword.slice(0, 16);
    
    setPassword(limitedPassword);
    
    // Clear password error when field is empty
    if (!limitedPassword) {
      setPasswordError('');
      return;
    }
    
    // Set error if password format is invalid
    if (!validatePassword(limitedPassword)) {
      if (limitedPassword.length < 8) {
        setPasswordError('Password must be at least 8 characters long');
      } else if (limitedPassword.length > 16) {
        setPasswordError('Password must not exceed 16 characters');
      } else {
        setPasswordError('Password must contain only alphanumeric characters (letters and numbers)');
      }
    } else {
      setPasswordError('');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset all errors first
    setError('');
    setEmailError('');
    setPasswordError('');
    
    // Check if both fields are empty
    if (!username && !password) {
      setEmailError('Please enter your email address');
      setPasswordError('Please enter your password');
      return;
    }
    
    // Validate email
    if (!username) {
      setEmailError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(username)) {
      setEmailError('Please enter a valid ONGC email address (must end with @ongc.co.in)');
      return;
    }
    
    // Validate password
    if (!password) {
      setPasswordError('Please enter your password');
      return;
    }
    
    if (!validatePassword(password)) {
      if (password.length < 8) {
        setPasswordError('Password must be at least 8 characters long');
      } else if (password.length > 16) {
        setPasswordError('Password must not exceed 16 characters');
      } else {
        setPasswordError('Password must contain only alphanumeric characters (letters and numbers)');
      }
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await login(username, password);
      
      // Get the return URL from query parameters, or default to home
      const queryParams = new URLSearchParams(location.search);
      const returnUrl = queryParams.get('returnUrl') || '/';
      navigate(returnUrl);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container-fluid login-container">
      <div className="row h-100 no-gutters">
        <div 
          className="col-md-8 p-0 platform-image"
          style={{ backgroundImage: `url(${platformImg})` }}
        ></div>
        
        <div className="col-md-4 login-form-container">
          <div className="ongc-logo">
            <img src={logo} alt="ONGC Logo" className="img-fluid" />
          </div>
          
          <div className="login-text">
            <h5>Welcome to booking</h5>
          </div>
          
          <div className="form-container">
            
            
            <form onSubmit={handleSubmit} className="login-form">
              <div className="mb-3">
                <label htmlFor="username" className="form-label">Username</label>
                <input
                  type="email"
                  className={`form-control ${emailError ? 'is-invalid' : ''}`}
                  id="username"
                  placeholder="user@ongc.co.in"
                  value={username}
                  onChange={handleEmailChange}
                  autoComplete="username"
                />
                {emailError && (
                  <div className="invalid-feedback">
                    {emailError}
                  </div>
                )}
              </div>
              
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                    id="password"
                    placeholder="8-16 alphanumeric characters"
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="current-password"
                    maxLength={16}
                  />
                  <button 
                    className="btn btn-outline-secondary" 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordError && (
                  <div className="invalid-feedback">
                    {passwordError}
                  </div>
                )}
                <div className="form-text text-muted">
                  Password must be 8-16 characters long and contain only letters and numbers
                </div>
              </div>
              {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
              <button 
                type="submit" 
                className="btn login-btn"
                disabled={loading}
              >
                {loading ? 'LOGGING IN...' : 'LOGIN NOW'}
              </button>
            </form>
            
            <div className="forgot-password">
              <Link to="/forgot-password">Forgot your password</Link>
            </div>
          </div>
          
          <div className="copyright">
            Copyright © 2025 ONGC. All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;